import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, AppCard } from '@/components/atoms';
import { type AccessoryOrderEntry } from '@/hooks/useAccessoryOrderItems';
import { formatUgx } from '@/utils/vehicleFormat';

import { orderTotals } from './accessoryOrderTotals';

type Props = {
  entries: AccessoryOrderEntry[];
  onClear?: () => void;
  /** Hides the actions on the checkout page, where the form below is the action. */
  showActions?: boolean;
};

/** Item count and a grand total. Nothing is charged here, so there are no tax or delivery lines. */
export function AccessoryOrderSummary({ entries, onClear, showActions = true }: Props) {
  const { lines, units, grandTotal, unpriced } = orderTotals(entries);

  return (
    <AppCard sx={{ p: 5, position: { lg: 'sticky' }, top: { lg: 112 } }}>
      <Stack spacing={3}>
        <Typography variant="h6" component="h2">
          Order summary
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.secondary">
            {lines} {lines === 1 ? 'part' : 'parts'} · {units} {units === 1 ? 'item' : 'items'}
          </Typography>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontWeight: 600 }}>Grand total</Typography>
          <Typography className="tabular-nums" sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.2 }}>
            {formatUgx(grandTotal)}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          This is the price of your parts, with no hidden charges. You pay nothing now. We confirm stock and fit with you first.
          {unpriced > 0 &&
            ` ${unpriced} ${unpriced === 1 ? 'part is' : 'parts are'} priced on request. We will quote them separately and they are not in this total.`}
        </Typography>
        {showActions && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <AppButton to="/accessories/checkout" size="large" fullWidth disabled={lines === 0}>
              Request order
            </AppButton>
            <AppButton to="/accessories" appVariant="secondary" fullWidth>
              Continue browsing
            </AppButton>
            {onClear && (
              <AppButton appVariant="ghost" size="small" onClick={onClear}>
                Clear list
              </AppButton>
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          Your order list is kept on this device only, so you can come back to it later.
        </Typography>
      </Stack>
    </AppCard>
  );
}
